import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, CircularProgress, Typography, Alert } from '@mui/material'
import { useAuth } from '../contexts/AuthContext'

function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { handleCallback, checkSession } = useAuth()
  const [error, setError] = useState(null)
  const processedRef = useRef(false)

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double execution in React StrictMode
      // OAuth codes are single-use, so we must only process once
      if (processedRef.current) {
        return
      }
      processedRef.current = true

      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const errorParam = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      // Handle OAuth error
      if (errorParam) {
        const message = errorDescription || errorParam
        navigate(`/login?error=${encodeURIComponent(message)}`, { replace: true })
        return
      }

      // Validate required parameters
      if (!code) {
        navigate('/login?error=Missing%20authorization%20code', { replace: true })
        return
      }

      try {
        // Exchange code for session via data-service
        const response = await fetch(`/api/admin/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state || '')}`, {
          method: 'GET',
          credentials: 'include',
        })

        const data = await response.json()

        if (!response.ok) {
          // If code exchange fails (e.g., code already used, expired, or replayed from browser cache),
          // check if we already have a valid session before showing an error
          await checkSession()
          navigate('/dashboard', { replace: true })
          return
        }

        // Callback successful - refresh auth state and redirect
        await handleCallback(data)
        navigate('/dashboard', { replace: true })

      } catch (err) {
        // Try checking session before redirecting to login
        // User might already be authenticated (e.g., browser replayed a cached callback URL)
        try {
          await checkSession()
          navigate('/dashboard', { replace: true })
        } catch {
          console.error('Auth callback error:', err)
          navigate(`/login?error=${encodeURIComponent(err.message)}`, { replace: true })
        }
      }
    }

    processCallback()
  }, [searchParams, navigate, handleCallback, checkSession])

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">Completing sign in...</Typography>
    </Box>
  )
}

export default AuthCallback
